import { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp, X, Loader2, CheckCircle2, AlertCircle, Upload } from 'lucide-react'
import { useUpload, type UploadEntry } from './UploadContext'
import { formatSize } from '@/lib/format'

function EntryRow({ entry, onCancel, onRetry }: { entry: UploadEntry; onCancel: (id: string) => void; onRetry: (id: string) => void }) {
  return (
    <div className="flex items-center gap-2 py-1.5 animate-fade-in">
      <div className="w-5 h-5 shrink-0 flex items-center justify-center">
        {entry.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin text-accent" strokeWidth={2} />}
        {entry.status === 'done' && <CheckCircle2 className="w-4 h-4 text-green-500" strokeWidth={2} />}
        {entry.status === 'error' && <AlertCircle className="w-4 h-4 text-red" strokeWidth={2} />}
        {entry.status === 'queued' && <Upload className="w-4 h-4 text-text-muted" strokeWidth={2} />}
        {entry.status === 'cancelled' && <X className="w-4 h-4 text-text-muted" strokeWidth={2} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate text-text">{entry.file.name}</p>
        {entry.status === 'uploading' && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 rounded-full bg-border overflow-hidden">
              <div className="h-full rounded-full bg-accent transition-all duration-300" style={{ width: `${entry.progress}%` }} />
            </div>
            <span className="text-[11px] text-text-muted shrink-0">{Math.round(entry.progress)}%</span>
            {entry.speed && <span className="text-[11px] text-text-muted shrink-0">{entry.speed}</span>}
          </div>
        )}
        {entry.status === 'done' && (
          <p className="text-[11px] text-green-600">{formatSize(entry.file.size)} · загружен</p>
        )}
        {entry.status === 'error' && (
          <p className="text-[11px] text-red truncate">{entry.error}</p>
        )}
        {entry.status === 'cancelled' && (
          <p className="text-[11px] text-text-muted">отменён</p>
        )}
        {entry.status === 'queued' && (
          <p className="text-[11px] text-text-muted">{formatSize(entry.file.size)} · ожидание</p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {entry.status === 'uploading' && (
          <button className="p-1 text-text-muted hover:text-red cursor-pointer rounded transition-colors" onClick={() => onCancel(entry.id)} title="Отменить">
            <X className="w-3 h-3" strokeWidth={2} />
          </button>
        )}
        {entry.status === 'error' && (
          <button className="text-[11px] text-accent hover:text-accent-hover cursor-pointer transition-colors" onClick={() => onRetry(entry.id)}>
            Повторить
          </button>
        )}
        {entry.status === 'done' && (
          <button className="p-1 text-text-muted hover:text-text cursor-pointer rounded transition-colors" onClick={() => onCancel(entry.id)} title="Удалить">
            <X className="w-3 h-3" strokeWidth={2} />
          </button>
        )}
      </div>
    </div>
  )
}

export function UploadTray() {
  const { entries, cancelUpload, retryUpload, clearCompleted, activeCount, completedCount } = useUpload()
  const [expanded, setExpanded] = useState(false)
  const prevActiveRef = useRef(0)

  // Auto-expand when new upload starts
  useEffect(() => {
    if (activeCount > prevActiveRef.current) {
      setExpanded(true)
    }
    prevActiveRef.current = activeCount
  }, [activeCount])

  if (entries.length === 0) return null

  const totalCount = entries.length
  const doneEntries = entries.filter((e) => e.status === 'done' || e.status === 'cancelled' || e.status === 'error')
  const uploading = entries.filter((e) => e.status === 'uploading')
  const avgSpeed = uploading.length > 0
    ? uploading.map((e) => e.speed).filter(Boolean).join(', ')
    : ''

  // Show active uploads first, then completed
  const sorted = [
    ...entries.filter((e) => e.status === 'uploading' || e.status === 'queued'),
    ...entries.filter((e) => e.status === 'done'),
    ...entries.filter((e) => e.status === 'error' || e.status === 'cancelled'),
  ]

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)]">
      <div className="rounded-xl border border-border bg-surface shadow-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-text hover:bg-surface-hover cursor-pointer transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <span className="flex items-center gap-2">
            <div className="relative">
              <Upload className="w-4 h-4" strokeWidth={2} />
              {activeCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 rounded-full bg-accent animate-pulse" />
              )}
            </div>
            {activeCount > 0
              ? `${activeCount} ${activeCount === 1 ? 'файл' : 'файла'} · ${avgSpeed || 'загрузка'}`
              : completedCount === totalCount
                ? `Загружено ${completedCount}`
                : `Файлы (${activeCount}/${totalCount})`}
          </span>
          {expanded ? <ChevronDown className="w-4 h-4" strokeWidth={2} /> : <ChevronUp className="w-4 h-4" strokeWidth={2} />}
        </button>

        {expanded && (
          <div className="px-3 pb-2 max-h-60 overflow-y-auto border-t border-border transition-all">
            {sorted.map((entry) => (
              <EntryRow key={entry.id} entry={entry} onCancel={cancelUpload} onRetry={retryUpload} />
            ))}
            {doneEntries.length > 0 && doneEntries.length === entries.length && (
              <button className="text-xs text-accent hover:text-accent-hover mt-1 cursor-pointer transition-colors" onClick={clearCompleted}>
                Очистить завершённые
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
